// NotesView — per-student, per-lesson blank canvas with pen, text, image,
// highlighter, eraser + undo/redo. Lightweight react-konva engine (intentionally
// slimmer than the teacher DeskeyPaint so students get a focused journal feel).
// Auto-saves to /api/student/lesson/{lessonId}/notes on every meaningful change.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Line as KLine, Text as KText, Image as KImage, Rect as KRect, Transformer } from 'react-konva';
import {
  Hand, Pen, Highlighter, Eraser, Type as TypeIcon, Image as ImgIcon, Undo2, Redo2,
  Save, Trash2, Download,
} from 'lucide-react';
import api from '../../studentApi';
import { toast } from 'sonner';
import { notifyError } from '../../../../lib/errors';
import { confirmDialog } from '../../../../components/ConfirmDialog';

// Minimal hook to load an HTMLImageElement from a src (data URL or URL).
function useHtmlImage(src) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const el = new window.Image();
    el.crossOrigin = 'anonymous';
    el.src = src;
    el.onload = () => setImg(el);
    return () => { el.onload = null; };
  }, [src]);
  return img;
}

const PALETTE = ['#0F172A', '#DC2626', '#EA580C', '#CA8A04', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#DB2777', '#475569'];
const TOOLS = [
  { id: 'select', label: 'Select / Move', Icon: Hand },
  { id: 'pen', label: 'Pen', Icon: Pen },
  { id: 'highlighter', label: 'Highlight', Icon: Highlighter },
  { id: 'text', label: 'Text', Icon: TypeIcon },
  { id: 'image', label: 'Image', Icon: ImgIcon },
  { id: 'eraser', label: 'Eraser', Icon: Eraser },
];

const STROKE_SIZES = [2, 4, 6, 10, 16];
const FONT_SIZES = [14, 18, 22, 28, 36, 48];

export default function NotesView({ lessonId, lessonTitle }) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const [shapes, setShapes] = useState([]);      // array of stroke/text/image objects
  const [history, setHistory] = useState([]);    // stack of prior shape states
  const [future, setFuture] = useState([]);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#0F172A');
  const [stroke, setStroke] = useState(4);
  const [fontSize, setFontSize] = useState(22);
  const [editingId, setEditingId] = useState(null);
  const [dims, setDims] = useState({ width: 1200, height: 800 });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // selected image, for resize handles
  const activeShapeRef = useRef(null);   // in-progress stroke during drag
  const trRef = useRef(null);            // Transformer node (image resize handles)
  const imageNodesRef = useRef({});      // shape id -> Konva image node, for the Transformer

  // Responsive canvas — fill exactly what's left in the viewport below the
  // canvas's own position (toolbar, tab bar, page padding all measured via
  // the container's actual offset, not guessed), so the whole Notes tool
  // fits on one desktop screen without the page needing to scroll. A fixed
  // 560px floor used to force a taller-than-available canvas — and hence a
  // page scrollbar — on any window shorter than ~820px.
  useEffect(() => {
    if (!containerRef.current) return;
    const BOTTOM_GUTTER = 44; // room for the "Auto-saves…" footer line below
    const computeHeight = () => {
      const top = containerRef.current.getBoundingClientRect().top;
      return Math.max(320, Math.floor(window.innerHeight - top - BOTTOM_GUTTER));
    };
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width);
        setDims({ width: w, height: computeHeight() });
      }
    });
    ro.observe(containerRef.current);
    const onResize = () => setDims(d => ({ ...d, height: computeHeight() }));
    window.addEventListener('resize', onResize);
    return () => { ro.disconnect(); window.removeEventListener('resize', onResize); };
  }, []);

  // Initial load
  useEffect(() => {
    let alive = true;
    api.get(`/student/lesson/${lessonId}/notes`)
      // Autosave waits for a real load, so a failed load never saves a blank canvas over saved notes.
      .then(d => { if (alive) { setShapes(d.canvas?.shapes || []); setSavedAt(d.updated_at); setLoaded(true); } })
      .catch(e => alive && notifyError(e, "Couldn't load your notes."));
    return () => { alive = false; };
  }, [lessonId]);

  // Leaving select mode (or the selected shape disappearing) drops the resize handles.
  useEffect(() => {
    if (tool !== 'select') { setSelectedId(null); return; }
    if (selectedId && !shapes.some(s => s.id === selectedId)) setSelectedId(null);
  }, [tool, selectedId, shapes]);

  // Attach/detach the Transformer to whichever image is selected.
  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const node = selectedId && tool === 'select' ? imageNodesRef.current[selectedId] : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, tool, shapes]);

  const pushHistory = useCallback((prev) => {
    setHistory(h => [...h.slice(-49), prev]);
    setFuture([]);
  }, []);

  const save = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/student/lesson/${lessonId}/notes`, {
        canvas: { shapes, width: dims.width, height: dims.height },
      });
      setSavedAt(r.updated_at);
      if (!silent) toast.success('Notes saved');
    } catch (e) {
      notifyError(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [lessonId, shapes, dims]);

  // Debounced autosave on shape changes (after initial load)
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => { save(true); }, 1200);
    return () => clearTimeout(t);
  }, [shapes, loaded, save]);

  // ── Interaction handlers ──────────────────────────────────────────────
  const onPointerDown = (e) => {
    if (tool === 'select') {
      // Only clear selection on empty-canvas clicks; clicks on a shape are
      // handled by that shape's own draggable/onClick/onDblClick handlers.
      if (e.target === e.target.getStage()) setSelectedId(null);
      return;
    }
    if (tool === 'text') {
      const pos = e.target.getStage().getPointerPosition();
      const id = `t_${Date.now()}`;
      pushHistory(shapes);
      setShapes(s => [...s, { id, kind: 'text', x: pos.x, y: pos.y, text: 'Type here…', fill: color, size: fontSize }]);
      setEditingId(id);
      return;
    }
    if (tool === 'image') return;   // image is added via file picker
    if (tool !== 'pen' && tool !== 'highlighter' && tool !== 'eraser') return;
    const pos = e.target.getStage().getPointerPosition();
    pushHistory(shapes);
    const s = {
      id: `s_${Date.now()}`,
      kind: 'stroke',
      points: [pos.x, pos.y],
      color: tool === 'eraser' ? '#FFFFFF' : color,
      stroke: tool === 'highlighter' ? stroke * 2.2 : stroke,
      opacity: tool === 'highlighter' ? 0.35 : 1,
      composite: tool === 'eraser' ? 'destination-out' : 'source-over',
    };
    activeShapeRef.current = s;
    setShapes(arr => [...arr, s]);
  };
  const onPointerMove = (e) => {
    if (!activeShapeRef.current) return;
    const pos = e.target.getStage().getPointerPosition();
    const id = activeShapeRef.current.id;
    setShapes(arr => arr.map(x => x.id === id ? { ...x, points: [...x.points, pos.x, pos.y] } : x));
  };
  const onPointerUp = () => { activeShapeRef.current = null; };

  const undo = () => {
    if (history.length === 0) return;
    setFuture(f => [shapes, ...f.slice(0, 49)]);
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setShapes(prev);
  };
  const redo = () => {
    if (future.length === 0) return;
    setHistory(h => [...h.slice(-49), shapes]);
    const nxt = future[0];
    setFuture(f => f.slice(1));
    setShapes(nxt);
  };

  const clearAll = async () => {
    if (!(await confirmDialog({ title: 'Clear notes', message: 'Clear all notes on this page? This cannot be undone.', danger: true, confirmLabel: 'Clear' }))) return;
    pushHistory(shapes);
    setShapes([]);
  };

  const handleImage = async (file) => {
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) return toast.error('Image must be under 2.5 MB.');
    const reader = new FileReader();
    reader.onload = (ev) => {
      pushHistory(shapes);
      const src = ev.target.result;
      // Drop at centre of viewport; default size 260x180 (preserving ratio)
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        const w = Math.min(320, dims.width * 0.4);
        const h = w / (ratio || 1);
        setShapes(s => [...s, { id: `i_${Date.now()}`, kind: 'image', src, x: (dims.width - w) / 2, y: 80, w, h }]);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const onTextChange = (id, value) => setShapes(arr => arr.map(x => x.id === id ? { ...x, text: value } : x));

  const exportPng = () => {
    try {
      const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
      const a = document.createElement('a');
      a.href = uri;
      a.download = `${(lessonTitle || 'notes').replace(/[^\w]+/g, '_')}.png`;
      a.click();
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="notes-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border p-2"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
        data-testid="notes-toolbar">
        <div className="flex gap-1">
          {TOOLS.map(({ id, label, Icon }) => {
            const a = tool === id;
            return (
              <button key={id}
                onClick={() => id === 'image' ? fileRef.current?.click() : setTool(id)}
                title={label}
                data-testid={`notes-tool-${id}`}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition"
                style={{ background: a ? 'var(--st-primary)' : 'var(--st-bg)', color: a ? '#fff' : 'inherit', border: '1px solid var(--st-border)' }}>
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleImage(e.target.files?.[0])} data-testid="notes-image-input" />

        <span className="w-px h-7 mx-1" style={{ background: 'var(--st-border)' }} />

        {/* Color palette */}
        <div className="flex items-center gap-1">
          {PALETTE.map(c => (
            <button key={c} onClick={() => setColor(c)}
              data-testid={`notes-color-${c.slice(1)}`}
              className="w-6 h-6 rounded-full border-2 transition"
              style={{ background: c, borderColor: color === c ? '#000' : 'var(--st-border)', transform: color === c ? 'scale(1.12)' : 'none' }} />
          ))}
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            className="w-6 h-6 rounded-full cursor-pointer ml-1" data-testid="notes-color-custom" />
        </div>

        <span className="w-px h-7 mx-1" style={{ background: 'var(--st-border)' }} />

        {/* Stroke width */}
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold opacity-60">Size</span>
          {STROKE_SIZES.map(s => (
            <button key={s} onClick={() => setStroke(s)}
              data-testid={`notes-stroke-${s}`}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition"
              style={{ background: stroke === s ? 'var(--st-primary)' : 'transparent', border: '1px solid var(--st-border)' }}>
              <span style={{ width: Math.min(s, 14), height: Math.min(s, 14), borderRadius: '50%', background: stroke === s ? '#fff' : color }} />
            </button>
          ))}
        </div>

        {tool === 'text' && (
          <>
            <span className="w-px h-7 mx-1" style={{ background: 'var(--st-border)' }} />
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold opacity-60">Text</span>
              <select value={fontSize} onChange={e => setFontSize(+e.target.value)}
                data-testid="notes-fontsize"
                className="text-xs rounded-lg px-2 py-1 border outline-none"
                style={{ background: 'var(--st-bg)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
                {FONT_SIZES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </>
        )}

        <div className="flex-1" />

        <button onClick={undo} disabled={!history.length}
          data-testid="notes-undo"
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5 disabled:opacity-30" title="Undo">
          <Undo2 className="w-4 h-4" />
        </button>
        <button onClick={redo} disabled={!future.length}
          data-testid="notes-redo"
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5 disabled:opacity-30" title="Redo">
          <Redo2 className="w-4 h-4" />
        </button>
        <button onClick={exportPng}
          data-testid="notes-export"
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5" title="Download PNG">
          <Download className="w-4 h-4" />
        </button>
        <button onClick={clearAll}
          data-testid="notes-clear"
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-red-500/10 text-red-500" title="Clear page">
          <Trash2 className="w-4 h-4" />
        </button>
        <button onClick={() => save(false)} disabled={saving}
          data-testid="notes-save"
          className="ml-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
          style={{ background: 'var(--st-primary)' }}>
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="rounded-2xl border overflow-hidden"
        style={{ borderColor: 'var(--st-border)', background: '#fafafa' }}
        data-testid="notes-canvas-container">
        <Stage
          ref={stageRef}
          width={dims.width}
          height={dims.height}
          onMouseDown={onPointerDown}
          onMousemove={onPointerMove}
          onMouseup={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
          style={{ cursor: tool === 'select' ? 'default' : tool === 'text' ? 'text' : (tool === 'image' ? 'copy' : 'crosshair') }}>
          {/* Static page background, on its own non-listening layer so the
              eraser's destination-out compositing only ever punches through
              drawn strokes — never the page fill/grid underneath it. */}
          <Layer listening={false}>
            <KRect x={0} y={0} width={dims.width} height={dims.height} fill="#ffffff" />
            {/* Subtle dot grid */}
            {Array.from({ length: Math.ceil(dims.width / 32) }).map((_, i) =>
              Array.from({ length: Math.ceil(dims.height / 32) }).map((_, j) => (
                <KRect key={`g-${i}-${j}`} x={i * 32} y={j * 32} width={1} height={1} fill="#e2e8f0" />
              ))
            )}
          </Layer>
          {/* Ink layer: pen/highlighter/eraser strokes only. Kept on its own
              canvas, permanently BELOW the text/image layer below, so the
              eraser's destination-out can only ever punch through ink —
              never through a placed image or text box (mirrors DeskeyPaint,
              where strokes flatten onto a raster layer sitting under the
              image objects). */}
          <Layer>
            {shapes.filter(sh => sh.kind === 'stroke').map(sh => (
              <KLine key={sh.id}
                points={sh.points}
                stroke={sh.color}
                strokeWidth={sh.stroke}
                opacity={sh.opacity}
                tension={0.35}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={sh.composite} />
            ))}
          </Layer>
          {/* Text + image layer, always on top of ink so eraser strokes can't affect them */}
          <Layer>
            {shapes.filter(sh => sh.kind !== 'stroke').map(sh => {
              if (sh.kind === 'text') {
                return (
                  <KText key={sh.id}
                    x={sh.x} y={sh.y}
                    text={sh.text}
                    fill={sh.fill}
                    fontSize={sh.size}
                    fontFamily="ui-sans-serif"
                    draggable={tool === 'select'}
                    onClick={() => { if (tool === 'select') setSelectedId(null); }}
                    onDragEnd={e => setShapes(arr => arr.map(x => x.id === sh.id ? { ...x, x: e.target.x(), y: e.target.y() } : x))}
                    onDblClick={() => setEditingId(sh.id)}
                    onDblTap={() => setEditingId(sh.id)} />
                );
              }
              if (sh.kind === 'image') {
                return (
                  <NotesImage key={sh.id} sh={sh} tool={tool}
                    onSelect={setSelectedId}
                    onChange={(u) => setShapes(arr => arr.map(x => x.id === sh.id ? { ...x, ...u } : x))}
                    registerNode={(node) => { imageNodesRef.current[sh.id] = node; }} />
                );
              }
              return null;
            })}
            <Transformer
              ref={trRef}
              rotateEnabled={false}
              boundBoxFunc={(oldBox, newBox) => (Math.abs(newBox.width) < 24 || Math.abs(newBox.height) < 24 ? oldBox : newBox)}
            />
          </Layer>
        </Stage>
        {editingId && (() => {
          const sh = shapes.find(x => x.id === editingId);
          if (!sh || sh.kind !== 'text') return null;
          return (
            <TextEditorOverlay
              shape={sh}
              onChange={(patch) => setShapes(arr => arr.map(x => x.id === sh.id ? { ...x, ...patch } : x))}
              onClose={() => setEditingId(null)}
              canvasWidth={dims.width}
              canvasHeight={dims.height}
            />
          );
        })()}
      </div>

      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--st-mute)' }}>
        <span>Auto-saves as you write.</span>
        <span>{savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}` : 'Not saved yet'}</span>
      </div>
    </div>
  );
}

// Konva image helper — loads source; drag to move and, once selected in the
// Select tool, resize via the shared <Transformer> handles.
function NotesImage({ sh, tool, onSelect, onChange, registerNode }) {
  const img = useHtmlImage(sh.src);
  const nodeRef = useRef(null);

  const select = () => { if (tool === 'select') onSelect(sh.id); };

  return (
    <KImage
      ref={node => { nodeRef.current = node; registerNode(node); }}
      image={img}
      x={sh.x} y={sh.y} width={sh.w} height={sh.h}
      draggable={tool === 'select'}
      onClick={select}
      onTap={select}
      onDragEnd={e => onChange({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={() => {
        const node = nodeRef.current;
        if (!node) return;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);
        onChange({
          x: node.x(),
          y: node.y(),
          w: Math.max(24, node.width() * scaleX),
          h: Math.max(24, node.height() * scaleY),
        });
      }} />
  );
}

// ────────────────────────────────────────────────────────────────────────
// Floating, draggable, resizable text-editor overlay for note text boxes.
// The Konva <Text> stays on the canvas, but while the user is editing we
// render a real HTML <textarea> on top so they get a proper IME-friendly,
// multi-line, resizable input. Drag the header bar to move. Drag the bottom-
// right grip to resize. "Done" closes and writes the text back to the shape.
// ────────────────────────────────────────────────────────────────────────
function TextEditorOverlay({ shape, onChange, onClose, canvasWidth, canvasHeight }) {
  // The editor's screen position lives in container-space (matches Konva
  // since our container is the canvas's bounding box). We track w/h so the
  // user can resize and we round-trip width back into the shape.
  const [pos, setPos] = useState({
    x: Math.max(0, Math.min(shape.x, canvasWidth - 220)),
    y: Math.max(0, Math.min(shape.y, canvasHeight - 110)),
  });
  const [size, setSize] = useState({ w: Math.max(240, shape.w || 280), h: Math.max(80, shape.h || 120) });
  const draggingRef = React.useRef(null);
  const resizingRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  // Auto-focus + select-all on first open so the placeholder is replaced
  // by typing immediately (fixes the "input not working" report — the
  // textarea now reliably receives the next keystroke).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    if (shape.text === 'Type here…') ta.select();
  }, [shape.text]);

  // Mouse handlers on window for smooth drag + resize even if the cursor
  // leaves the editor briefly. We attach once and stop on mouseup.
  useEffect(() => {
    const onMove = (e) => {
      if (draggingRef.current) {
        const d = draggingRef.current;
        const nx = Math.max(0, Math.min(canvasWidth - size.w, d.startPos.x + (e.clientX - d.startMouse.x)));
        const ny = Math.max(0, Math.min(canvasHeight - size.h, d.startPos.y + (e.clientY - d.startMouse.y)));
        setPos({ x: nx, y: ny });
      } else if (resizingRef.current) {
        const r = resizingRef.current;
        const nw = Math.max(180, r.startSize.w + (e.clientX - r.startMouse.x));
        const nh = Math.max(60, r.startSize.h + (e.clientY - r.startMouse.y));
        setSize({ w: nw, h: nh });
      }
    };
    const onUp = () => {
      if (draggingRef.current || resizingRef.current) {
        // Commit final position/size back to the konva shape so it sticks.
        onChange({ x: pos.x, y: pos.y, w: size.w, h: size.h });
      }
      draggingRef.current = null;
      resizingRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [canvasWidth, canvasHeight, size.w, size.h, pos.x, pos.y, onChange]);

  const startDrag = (e) => {
    e.preventDefault();
    draggingRef.current = { startMouse: { x: e.clientX, y: e.clientY }, startPos: { ...pos } };
  };
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { startMouse: { x: e.clientX, y: e.clientY }, startSize: { ...size } };
  };

  return (
    <div
      data-testid="notes-text-overlay"
      className="absolute z-10 rounded-lg shadow-lg overflow-hidden flex flex-col"
      style={{
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        background: '#ffffff',
        border: `2px solid ${shape.fill}`,
      }}
      onMouseDown={(e) => e.stopPropagation()}    /* don't let drag start a stroke */
    >
      {/* Drag handle / header */}
      <div
        onMouseDown={startDrag}
        className="flex items-center justify-between px-2 py-1 cursor-move select-none text-xs font-semibold text-white flex-shrink-0"
        style={{ background: shape.fill }}
        data-testid="notes-text-overlay-handle">
        <span>Editing text — drag to move</span>
        <button onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          data-testid="notes-text-overlay-done"
          className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 transition-colors text-xs">
          Done
        </button>
      </div>

      {/* The actual text area */}
      <textarea
        ref={textareaRef}
        value={shape.text}
        onChange={e => onChange({ text: e.target.value })}
        onMouseDown={e => e.stopPropagation()}
        data-testid="notes-text-editor"
        className="flex-1 p-2 outline-none resize-none w-full"
        style={{
          fontSize: shape.size,
          color: shape.fill,
          background: '#ffffff',
          fontFamily: 'ui-sans-serif, system-ui',
          lineHeight: 1.35,
        }}
      />

      {/* Resize grip */}
      <div
        onMouseDown={startResize}
        data-testid="notes-text-overlay-resize"
        title="Drag to resize"
        className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
        style={{
          background: `linear-gradient(135deg, transparent 50%, ${shape.fill} 50%)`,
        }}
      />
    </div>
  );
}
